import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import TransactionDetail from "@/pages/TransactionDetail";
import WithdrawalWizard from "@/pages/WithdrawalWizard";
import Contact from "@/pages/Contact";
import Account from "@/pages/Account";
import KybWizard from "@/pages/KybWizard";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUserList from "@/pages/admin/AdminUserList";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import AdminFeeSettings from "@/pages/admin/AdminFeeSettings";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";

function Protected() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  // Admin accounts land on /admin, not on the exporter dashboard. They can
  // still navigate here manually (the /admin ↔ /dashboard toggle) but a
  // "landing here fresh from login" flow always deep-links to /admin.
  if (user.is_admin) return <Navigate to="/admin" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return <Outlet />;
}

// AdminGate: bypasses the OnboardingGate — admins don't need to onboard
// themselves as an exporter. Server-side, every /api/admin/* route re-checks
// is_admin off the DB record, so hiding the UI is only cosmetic.
function AdminGate() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function OnboardingGate() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  // Admins never onboard as exporters — always shunt them to /admin.
  if (user.is_admin) return <Navigate to="/admin" replace />;
  if (user.onboarded) return <Navigate to="/dashboard" replace />;
  return <Onboarding />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/onboarding" element={<OnboardingGate />} />
            <Route element={<Protected />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/transactions/:id" element={<TransactionDetail />} />
              <Route path="/transactions/:id/withdraw" element={<WithdrawalWizard />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/account" element={<Account />} />
              <Route path="/kyb" element={<KybWizard />} />
            </Route>
            <Route element={<AdminGate />}>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminOverview />} />
                <Route path="users" element={<AdminUserList />} />
                <Route path="users/:id" element={<AdminUserDetail />} />
                <Route path="settings/fee" element={<AdminFeeSettings />} />
                <Route path="audit" element={<AdminAuditLog />} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
